/**
 * Did the library get this meal right? (#1336)
 *
 * ⚠️ SEPARATE FROM HowWasIt ON PURPOSE. That one asks each person whether they
 * liked dinner. This asks the household one thing about the MEAL — and the two
 * must not merge, because HowWasIt earns its completion rate by being a single
 * tap per person, and bolting five more questions onto it would quietly kill
 * the feature that already works.
 *
 * ⛔ ONE QUESTION IS THE WHOLE DESIGN. "Did it work as two plates" is the only
 * claim this product makes that a real kitchen can falsify, and it saves on the
 * tap. Everything else lives behind "Add detail" and most people will never open
 * it — which is correct. A form nobody finishes teaches the library nothing.
 *
 * ⚠️ Nothing here changes anybody's tastes, same as HowWasIt, and for the same
 * reason: what a person SAID they eat stays where they put it.
 */
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { twoPlates, type MealMarkInput } from "../api/twoPlates";

export function DidItWork({ mealId, authoredMinutes }: { mealId: number; authoredMinutes?: number }) {
  const [answered, setAnswered] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const [minutes, setMinutes] = useState("");
  const [reheated, setReheated] = useState<boolean | null>(null);
  const [again, setAgain] = useState<boolean | null>(null);
  const [swaps, setSwaps] = useState("");
  const [savedDetail, setSavedDetail] = useState(false);

  const save = useMutation({
    mutationFn: (body: MealMarkInput) => twoPlates.markMeal(mealId, body),
  });

  const answer = (worked: boolean) => {
    setAnswered(worked);
    save.mutate({ forkWorked: worked });
  };

  const saveDetail = () => {
    const n = Number(minutes);
    save.mutate(
      {
        // ⛔ Only send what was actually answered. Sending nulls for untouched
        // fields would record "no" on behalf of somebody who said nothing.
        ...(minutes.trim() && Number.isFinite(n) && n > 0 ? { actualMinutes: Math.round(n) } : {}),
        ...(reheated !== null ? { reheatedWell: reheated } : {}),
        ...(again !== null ? { wouldCookAgain: again } : {}),
        ...(swaps.trim() ? { swaps: swaps.trim() } : {}),
      },
      { onSuccess: () => setSavedDetail(true) },
    );
  };

  const Choice = ({
    on,
    onClick,
    children,
  }: {
    on: boolean;
    onClick: () => void;
    children: React.ReactNode;
  }) => (
    <button
      onClick={onClick}
      aria-pressed={on}
      className="border-line flex-1 rounded-lg border py-2 text-xs font-medium"
      style={on ? { borderColor: "var(--color-plate-b)", color: "var(--color-plate-b)" } : undefined}
    >
      {children}
    </button>
  );

  return (
    <div className="border-line mt-2 rounded-lg border p-3">
      {answered === null ? (
        <>
          <p className="text-muted text-xs uppercase tracking-wide">Did it work as two plates?</p>
          <div className="mt-2.5 flex gap-1.5">
            <Choice on={false} onClick={() => answer(true)}>
              Yes
            </Choice>
            <Choice on={false} onClick={() => answer(false)}>
              Not really
            </Choice>
          </div>
          <p className="text-muted mt-2 text-[11px]">
            One pot, everybody fed. Tell us if it didn't hold up — that's how the
            library gets fixed.
          </p>
        </>
      ) : (
        <>
          <p className="text-sm">
            {answered ? "Good — noted." : "Noted. We'll look at that one."}
          </p>

          {!open && !savedDetail && (
            <button
              onClick={() => setOpen(true)}
              className="text-muted mt-1 text-xs underline underline-offset-4"
            >
              Add detail
            </button>
          )}

          {open && !savedDetail && (
            <div className="mt-3 flex flex-col gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-muted text-xs">
                  Hands-on minutes
                  {/* The authored number, shown so the answer is a correction
                      rather than a guess into the void. */}
                  {authoredMinutes ? ` — we said ${authoredMinutes}` : ""}
                </span>
                <input
                  inputMode="numeric"
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder={authoredMinutes ? String(authoredMinutes) : "30"}
                  className="border-line rounded-lg border px-3 py-2 text-sm"
                />
              </label>

              <div>
                <span className="text-muted text-xs">Did it reheat for lunch?</span>
                <div className="mt-1.5 flex gap-1.5">
                  <Choice on={reheated === true} onClick={() => setReheated(true)}>
                    Yes
                  </Choice>
                  <Choice on={reheated === false} onClick={() => setReheated(false)}>
                    No
                  </Choice>
                </div>
              </div>

              <div>
                <span className="text-muted text-xs">Would you cook it again?</span>
                <div className="mt-1.5 flex gap-1.5">
                  <Choice on={again === true} onClick={() => setAgain(true)}>
                    Yes
                  </Choice>
                  <Choice on={again === false} onClick={() => setAgain(false)}>
                    No
                  </Choice>
                </div>
              </div>

              <label className="flex flex-col gap-1">
                <span className="text-muted text-xs">Swapped anything?</span>
                <input
                  value={swaps}
                  onChange={(e) => setSwaps(e.target.value)}
                  maxLength={300}
                  placeholder="Turkey instead of beef"
                  className="border-line rounded-lg border px-3 py-2 text-sm"
                />
              </label>

              <button
                onClick={saveDetail}
                disabled={save.isPending}
                className="bg-plate-b text-ground self-start rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-40"
              >
                {save.isPending ? "Saving…" : "Save"}
              </button>
            </div>
          )}

          {savedDetail && <p className="text-muted mt-1 text-xs">Thanks — that helps.</p>}
        </>
      )}

      {save.isError && (
        <p role="alert" className="text-warn mt-2 text-sm">
          That did not save. Try again.
        </p>
      )}
    </div>
  );
}
